/**
 * Kickard PHASE 5.0B-2B — Home Premium Final Match Pass.
 *
 * Corrects Phase 5.0B-2 composition against the authoritative
 * HOME_TARGET.png:
 *   - compact player header (no duplicate division pill)
 *   - tighter Kickard logo hero
 *   - premium championship-gold PLAY button (CSS reproduction of
 *     play-button-REFERENCE.png)
 *   - Ranked + Weekly Cup as compact SIDE-BY-SIDE cards on mobile
 *   - premium rank crest artwork with LIVE division numeral overlay
 *     (reuses the existing Kickard tier/division representation:
 *      1→I, 2→II, 3→III; legend/unranked have no numeral)
 *   - premium Weekly Cup trophy artwork
 *   - one-row utility (Profile / How To Play / Settings) preserved
 *   - compact footer motto — targets no vertical scroll at 390–430 px
 *
 * ZERO functional change: all data sources, routes, telemetry, dev
 * toggle, install prompt, sound state and competitive fallback
 * semantics are preserved byte-for-byte.
 */
import React, { useEffect, useState, useRef } from 'react';
import {
  Volume2, VolumeX, User, BookOpen, Settings, ChevronRight,
  Clock, BarChart3,
} from 'lucide-react';
import { TID } from '@/constants/testIds';
import { storage } from '@/lib/storage';
import { sounds } from '@/audio/sounds';
import { track, EVT } from '@/lib/telemetry';
import { setScreen } from '@/lib/sessionTracking';
import {
  getRankedAvailability,
  getMyRankedState,
  getRankedLeaderboard,
} from '@/api/rankedClient';
import { fetchWeeklyCup } from '@/api/weeklyCupClient';
import { loadGuest } from '@/lib/guestIdentity';
import InstallPrompt from '@/components/InstallPrompt';
import { Panel } from '@/components/v5b';
import { TIER_ART, crestNumeralStyle, CREST_PLACEHOLDER_STYLE } from '../lib/rankCrest';

// Production presentation constants.
const STADIUM_ASSET = '/home-stadium.webp';
const LOGO_ASSET = '/kickard-logo.webp';
const PLAYER_EMBLEM = '/kickard/player-emblem.webp';
const WEEKLY_TROPHY = '/kickard/weekly-cup-trophy.webp';

// Premium tier → crest artwork mapping. Master / Champion have no
// dedicated art in the current premium pack — they inherit the
// closest neighbouring crest until final art lands (Phase 5.0C).
// Tier accents — used only inside Ranked identity, never on Kickard
// brand-gold surfaces (PLAY / logo / Weekly Cup remain brand-gold).
const TIER_ACCENT = {
  bronze:   '#CD7F32',
  silver:   '#C7CDD4',
  gold:     '#D4AF37',
  platinum: '#55BCEB',
  diamond:  '#9A55E7',
  master:   '#9A55E7',
  champion: '#EF4A3D',
  legend:   '#EF4A3D',
};
// Live division → roman numeral. Mirrors the existing Kickard
// representation in CompetitiveSummary.jsx / CompetitiveResultBlock.jsx.
const DIVISION_ROMAN = { 1: 'I', 2: 'II', 3: 'III' };

export default function HomeScreen({
  onPlay, onPlayOnline, onTutorial, onSettings, onProfile, onLeaderboard,
  onToggleDev, onWeeklyCup,
}) {
  const tapsRef = useRef([]);
  const [soundOn, setSoundOn] = useState(() => storage.getSettings().soundEnabled);
  const [rankedAvail, setRankedAvail] = useState(null);
  const [rankedMe, setRankedMe] = useState({ loading: true, data: null });
  const [rankedPos, setRankedPos] = useState(null);
  const [rankedSeasonName, setRankedSeasonName] = useState(null);
  const [weeklyMe, setWeeklyMe] = useState({ loading: true, data: null });
  const [countdown, setCountdown] = useState('');

  useEffect(() => { setScreen('home'); track(EVT.HOME_LOADED); }, []);

  // Data fetches — identical to 5.0A Pass 1B.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try { const a = await getRankedAvailability(); if (!cancelled) setRankedAvail(a); }
      catch (_) { /* ignore */ }
    })();
    const guest = loadGuest();
    const guestId = guest?.guestId || null;
    if (!guestId) {
      setRankedMe({ loading: false, data: null });
      setWeeklyMe({ loading: false, data: null });
      return () => { cancelled = true; };
    }
    (async () => {
      try {
        const r = await getMyRankedState(guestId);
        if (!cancelled) setRankedMe({ loading: false, data: r?.ok ? r : null });
      } catch (_) { if (!cancelled) setRankedMe({ loading: false, data: null }); }
    })();
    (async () => {
      try {
        const lb = await getRankedLeaderboard({ guestId, limit: 100 });
        if (cancelled || !lb?.ok) return;
        setRankedSeasonName(lb?.season?.name || 'Preseason');
        if (lb?.viewerRow?.rank != null) setRankedPos(lb.viewerRow.rank);
        else if (Array.isArray(lb?.rows)) {
          const mine = lb.rows.find((r) => r.guestId === guestId);
          if (mine?.rank != null) setRankedPos(mine.rank);
        }
      } catch (_) { /* silent */ }
    })();
    (async () => {
      try {
        const w = await fetchWeeklyCup({ guestId });
        if (!cancelled) setWeeklyMe({ loading: false, data: w?.ok !== false ? w : null });
      } catch (_) { if (!cancelled) setWeeklyMe({ loading: false, data: null }); }
    })();
    return () => { cancelled = true; };
  }, []);

  // Weekly Cup countdown → next Monday 00:00 UTC.
  useEffect(() => {
    const tick = () => {
      const now = new Date();
      const utcDow = now.getUTCDay();
      const daysToMon = (8 - (utcDow === 0 ? 7 : utcDow)) % 7 || 7;
      const next = new Date(Date.UTC(
        now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + daysToMon,
        0, 0, 0, 0,
      ));
      const diff = Math.max(0, next.getTime() - now.getTime());
      const d = Math.floor(diff / 86400000);
      const h = Math.floor((diff % 86400000) / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      setCountdown(`${String(d).padStart(2, '0')}d ${String(h).padStart(2, '0')}h ${String(m).padStart(2, '0')}m`);
    };
    tick();
    const id = setInterval(tick, 30_000);
    return () => clearInterval(id);
  }, []);

  const toggleSound = () => {
    const next = !soundOn;
    setSoundOn(next);
    storage.setSettings({ soundEnabled: next });
    sounds.setEnabled(next);
    if (next) { sounds.unlock(); sounds.uiTap(); }
  };

  // Dev toggle — 5 rapid taps on the logo (unchanged from 5.0A).
  const onLogoTap = () => {
    sounds.unlock();
    const now = Date.now();
    tapsRef.current = [...tapsRef.current.filter((t) => now - t < 2500), now];
    if (tapsRef.current.length >= 5) {
      tapsRef.current = [];
      const s = storage.getSettings();
      storage.setSettings({ devEnabled: !s.devEnabled });
      onToggleDev?.(!s.devEnabled);
    }
  };

  // Ranked availability state machine (BUG — API ERROR must NOT be
  // conflated with "Ranked globally closed"):
  //   • `rankedAvailabilityKnown` is true ONLY when the server
  //     authoritatively responded ok. Fetch failures / network errors
  //     / Safari "Load failed" / still-loading leave this `false`.
  //   • `rankedEnabled` requires the authoritative response AND both
  //     flags set.
  //   • `rankedClosedExplicit` is the ONLY case the UI is allowed to
  //     render the "Ranked opens soon" string.
  const rankedAvailabilityKnown = rankedAvail?.ok === true;
  const rankedEnabled = !!(rankedAvailabilityKnown
                          && rankedAvail.enabled
                          && rankedAvail.activeSeasonExists);
  const rankedClosedExplicit = rankedAvailabilityKnown && !rankedEnabled;
  // PHASE 5.0B — Astrix repro: do NOT display `Placement 0/10` when
  // the authoritative `/api/ranked/me` response hasn't successfully
  // loaded. We only trust `placement` when the fetch finished with a
  // real payload. During loading / transient error we show a neutral
  // em-dash so the UI never fabricates a placement progress number.
  const rankedMeLoaded = rankedMe?.data?.ok === true;
  const rank = rankedMe.data?.rank || {};
  const placement = rankedMe.data?.placement || {};
  const record = rankedMe.data?.record || {};
  // STRICT: only true when the authoritative server payload explicitly
  // says placementComplete === true. Previous `!== false` defaulted to
  // true during the fetch window (placement = {}) and caused a brief
  // misleading "earned rank" state to flash on Home.
  const placementComplete = placement.placementComplete === true;
  const rawTier = rank.currentTier ? String(rank.currentTier).toLowerCase() : null;
  const division = rank.currentDivision ?? null;
  // Prefer the server's precomputed label; only fall back to a local
  // reconstruction so display is 100 % consistent with the rest of
  // the Ranked system (ProfileScreen, RankedLobbyScreen).
  const fullLabel = rank.currentLabel || buildTierLabel(rawTier, division);
  const sr = rank.currentSR ?? null;
  const seasonDisplay = rankedSeasonName || rankedMe.data?.season?.name || 'Preseason';
  // "Has earned a real rank" — only true if the authoritative payload
  // ALL agrees: placement complete, server returned a non-unranked
  // currentTier, and a numeric currentSR is present. This prevents a
  // fallback/default "Bronze III · 0 SR" from ever being rendered as
  // an earned rank when the server hasn't yet reported a real one.
  const hasEarnedRank = !!(
    placementComplete
    && rawTier
    && rawTier !== 'unranked'
    && sr != null
    && Number(record.rankedMatchesPlayed || 0) > 0
  );

  const guest = loadGuest();
  const displayName = guest?.displayName || 'Player';

  const weeklyRow = weeklyMe.data?.viewerRow
    || (Array.isArray(weeklyMe.data?.leaderboard) && guest?.guestId
        ? weeklyMe.data.leaderboard.find((r) => r.guestId === guest.guestId)
        : null);

  const headerLabel = hasEarnedRank
    ? `${fullLabel} · ${sr} SR`
    : rankedMe.loading
      ? 'Loading…'
      : (placement.placementMatchesPlayed != null
          ? `Placement ${placement.placementMatchesPlayed}/${placement.placementMatchesRequired ?? 10}`
          : 'Unranked');

  // ---------------------------------------------------------
  // Presentation
  // ---------------------------------------------------------
  return (
    <div
      data-testid={TID.home}
      className="relative w-full mx-auto flex flex-col overflow-x-hidden"
      style={{ maxWidth: 430, minHeight: '100dvh' }}
    >
      {/* Stadium background */}
      <div
        aria-hidden
        className="absolute inset-0 z-0"
        style={{
          backgroundImage: `url(${STADIUM_ASSET})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center top',
          backgroundRepeat: 'no-repeat',
        }}
      />
      {/* Readability overlay — slightly lightened vs 5.0B-2C so the
          stadium remains legible on lower-brightness phones. */}
      <div
        aria-hidden
        className="absolute inset-0 z-0"
        style={{
          background: `linear-gradient(180deg,
              rgba(0,0,0,0.05) 0%,
              rgba(0,0,0,0.16) 40%,
              rgba(0,0,0,0.44) 78%,
              rgba(0,0,0,0.70) 100%)`,
        }}
      />

      <div
        className="relative z-10 flex flex-col px-3 gap-2.5"
        style={{ paddingTop: 10, paddingBottom: 12 }}
      >
        {/* ============ TOP: PLAYER IDENTITY + SOUND ============ */}
        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={() => onProfile?.()}
            data-testid="home-player-identity-btn"
            aria-label={`Open my profile, ${displayName}`}
            className="flex items-center gap-2.5 min-w-0 p-0 m-0 bg-transparent border-0 text-left cursor-pointer active:opacity-80"
            style={{ transition: 'opacity 120ms ease', WebkitTapHighlightColor: 'transparent' }}
          >
            <img
              src={PLAYER_EMBLEM}
              alt=""
              aria-hidden
              data-testid="home-player-emblem"
              className="flex-shrink-0"
              style={{
                width: 38, height: 38,
                filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.55))',
              }}
            />
            <div className="leading-tight min-w-0" data-testid="home-player-identity">
              <div
                className="text-white font-semibold truncate"
                style={{
                  fontSize: 16, letterSpacing: '0.01em',
                  textShadow: '0 1px 4px rgba(0,0,0,0.7)',
                }}
              >
                {displayName}
              </div>
              <div
                className="kk-type-ui truncate mt-0.5"
                style={{
                  fontSize: 11, letterSpacing: '0.06em',
                  color: 'rgba(255,255,255,0.85)',
                }}
                data-testid="home-player-rank-line"
              >
                {headerLabel}
              </div>
            </div>
          </button>
          <button
            type="button"
            onClick={toggleSound}
            data-testid="sound-toggle-home"
            aria-label={soundOn ? 'Mute' : 'Enable sound'}
            className="w-9 h-9 rounded-full flex items-center justify-center border border-white/15 bg-black/45 flex-shrink-0"
            style={{ color: soundOn ? 'hsl(var(--kk-gold-bright))' : 'rgba(255,255,255,0.45)' }}
          >
            {soundOn ? <Volume2 size={15} /> : <VolumeX size={15} />}
          </button>
        </div>

        {/* ============ BRAND HERO ============ */}
        <button
          type="button"
          onClick={onLogoTap}
          aria-label="Kickard"
          data-testid={TID.logo}
          className="mx-auto flex flex-col items-center focus:outline-none"
          style={{
            width: 'min(62%, 240px)',
            marginTop: 2,
          }}
        >
          <img
            src={LOGO_ASSET}
            alt="Kickard"
            style={{
              width: '100%', height: 'auto',
              filter: 'drop-shadow(0 6px 22px rgba(0,0,0,0.6))',
            }}
          />
        </button>
        <div className="text-center" data-testid="home-hero-taglines" style={{ marginTop: -2 }}>
          <div
            className="kk-type-display"
            style={{
              color: 'hsl(var(--kk-gold-bright))',
              fontSize: 15, letterSpacing: '0.30em',
              textShadow: '0 1px 6px rgba(0,0,0,0.6)',
            }}
          >
            FOOTBALL MEETS CARDS
          </div>
          <div
            className="kk-type-ui uppercase"
            style={{
              color: 'rgba(255,255,255,0.86)',
              fontSize: 10, letterSpacing: '0.26em',
              marginTop: 4,
            }}
          >
            Same rules. A deeper game.
          </div>
        </div>

        {/* ============ PLAY CTA — premium championship gold ============ */}
        <button
          type="button"
          data-testid="home-play-btn"
          onClick={() => {
            track(EVT.HOME_CTA_CLICKED, { metadata: { cta: 'play_hub' } });
            onPlayOnline ? onPlayOnline() : onPlay?.();
          }}
          className="relative w-full flex items-center justify-center focus:outline-none"
          style={{
            marginTop: 6,
            height: 62,
            borderRadius: 12,
            border: '1px solid #6a4a12',
            background:
              'linear-gradient(180deg, #ffe27a 0%, #f4c542 42%, #d99a1c 100%)',
            boxShadow:
              '0 0 0 1px rgba(255, 220, 130, 0.35) inset,'
              + '0 1px 0 rgba(255, 240, 180, 0.85) inset,'
              + '0 -6px 12px rgba(90, 55, 5, 0.35) inset,'
              + '0 8px 22px rgba(0, 0, 0, 0.55),'
              + '0 0 34px rgba(212, 175, 55, 0.25)',
          }}
        >
          <span
            className="kk-type-display"
            style={{
              color: '#1a0f00',
              fontSize: 28,
              letterSpacing: '0.24em',
              textShadow: '0 1px 0 rgba(255, 235, 170, 0.4)',
            }}
          >
            PLAY
          </span>
          <span
            aria-hidden
            className="absolute flex items-center justify-center"
            style={{ right: 16, color: '#1a0f00' }}
          >
            <ChevronRight size={26} strokeWidth={3.2} />
          </span>
        </button>

        {/* ============ COMPETITIVE — RANKED + WEEKLY (side-by-side) ============ */}
        <div className="grid grid-cols-2 gap-2 mt-1">
          {/* --- Ranked Preseason --- */}
          <Panel
            variant="competitive"
            edgeTop
            testId="home-ranked-card"
            onClick={!rankedClosedExplicit ? onLeaderboard : undefined}
            className="cursor-pointer flex flex-col"
            style={{
              padding: 10,
              background:
                'linear-gradient(180deg,'
                + ' hsl(38 22% 18% / 0.55) 0%,'
                + ' hsl(220 18% 14% / 0.9) 60%,'
                + ' hsl(220 18% 11% / 0.9) 100%)',
              borderColor: 'rgba(212, 175, 55, 0.28)',
            }}
          >
            <div className="flex items-start gap-2">
              <RankCrest
                tier={hasEarnedRank ? rawTier : null}
                division={hasEarnedRank ? division : null}
                testId="home-ranked-crest"
              />
              <div className="flex-1 min-w-0">
                <div
                  className="kk-type-ui uppercase"
                  style={{
                    fontSize: 9, letterSpacing: '0.14em',
                    color: 'rgba(255,255,255,0.7)',
                    whiteSpace: 'nowrap',
                  }}
                >
                  Ranked Preseason
                </div>
                {rankedEnabled && hasEarnedRank ? (
                  <>
                    <div
                      className="kk-type-display leading-none truncate"
                      style={{
                        color: TIER_ACCENT[rawTier] || 'hsl(var(--kk-gold-bright))',
                        fontSize: 20,
                        marginTop: 3,
                      }}
                      data-testid="home-ranked-tier"
                    >
                      {fullLabel}
                    </div>
                    <div
                      className="kk-type-numeric text-white leading-none"
                      style={{ fontSize: 13, marginTop: 4 }}
                      data-testid="home-ranked-sr"
                    >
                      {sr != null ? `${sr} SR` : '— SR'}
                    </div>
                  </>
                ) : !rankedClosedExplicit ? (
                  // Covers both LOADING and NETWORK-ERROR states.
                  // Never collapse these into "Ranked opens soon".
                  // Only show real placement progress when `/api/ranked/me`
                  // has authoritatively resolved; otherwise show a
                  // neutral em-dash (no fabricated `0/10`).
                  <div
                    className="kk-type-display text-white leading-none"
                    style={{ fontSize: 15, marginTop: 3 }}
                    data-testid="home-ranked-placement"
                  >
                    {rankedMeLoaded && placement.placementMatchesPlayed != null
                      ? `Placement ${placement.placementMatchesPlayed}/${placement.placementMatchesRequired ?? 10}`
                      : '—'}
                  </div>
                ) : (
                  <div
                    className="kk-type-ui text-white/60"
                    style={{ fontSize: 11, marginTop: 3 }}
                    data-testid="home-ranked-empty"
                  >
                    Ranked opens soon
                  </div>
                )}
              </div>
            </div>
            <hr className="kk-divider" style={{ margin: '8px 0' }} />
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1.5 min-w-0">
                <BarChart3 size={14} className="text-[hsl(var(--kk-gold-bright))] flex-shrink-0" />
                <div className="min-w-0">
                  <div
                    className="kk-type-display text-white leading-none"
                    style={{ fontSize: 15 }}
                    data-testid="home-ranked-position"
                  >
                    {rankedPos != null ? `#${rankedPos}` : '—'}
                  </div>
                  <div
                    className="kk-type-ui uppercase truncate"
                    style={{ fontSize: 9, letterSpacing: '0.20em', color: 'rgba(255,255,255,0.68)', marginTop: 2 }}
                  >
                    {seasonDisplay}
                  </div>
                </div>
              </div>
              <ChevronRight size={14} className="text-white/40 flex-shrink-0" />
            </div>
          </Panel>

          {/* --- Weekly Cup --- */}
          <Panel
            variant="competitive"
            edgeTop
            testId="home-weekly-card"
            onClick={onWeeklyCup}
            className="cursor-pointer flex flex-col"
            style={{
              padding: 10,
              background:
                'linear-gradient(180deg,'
                + ' hsl(38 22% 18% / 0.55) 0%,'
                + ' hsl(220 18% 14% / 0.9) 60%,'
                + ' hsl(220 18% 11% / 0.9) 100%)',
              borderColor: 'rgba(212, 175, 55, 0.28)',
            }}
          >
            <div className="flex items-start gap-2">
              <img
                src={WEEKLY_TROPHY}
                alt=""
                aria-hidden
                data-testid="home-weekly-trophy"
                className="flex-shrink-0"
                style={{
                  width: 52, height: 52, objectFit: 'contain',
                  filter:
                    'drop-shadow(0 3px 8px rgba(0,0,0,0.55))'
                    + ' drop-shadow(0 0 8px rgba(212, 175, 55, 0.28))',
                }}
              />
              <div className="flex-1 min-w-0">
                <div
                  className="kk-type-ui uppercase"
                  style={{
                    fontSize: 9, letterSpacing: '0.14em',
                    color: 'rgba(255,255,255,0.7)',
                    whiteSpace: 'nowrap',
                  }}
                >
                  Weekly Cup
                </div>
                {weeklyMe.loading ? (
                  <div className="kk-type-ui text-white/50" style={{ fontSize: 11, marginTop: 3 }}>
                    Loading…
                  </div>
                ) : weeklyRow ? (
                  <>
                    <div className="flex items-baseline gap-1.5" style={{ marginTop: 3 }}>
                      <span
                        className="kk-type-display leading-none"
                        style={{ color: 'hsl(var(--kk-gold-bright))', fontSize: 20 }}
                        data-testid="home-weekly-position"
                      >
                        #{weeklyRow.rank ?? weeklyRow.position ?? '—'}
                      </span>
                      <span
                        className="kk-type-display text-white leading-none"
                        style={{ fontSize: 15 }}
                        data-testid="home-weekly-points"
                      >
                        {weeklyRow.weeklyPoints ?? weeklyRow.points ?? 0} PTS
                      </span>
                    </div>
                    <div
                      className="kk-type-ui text-white/85"
                      style={{ fontSize: 10.5, marginTop: 3 }}
                      data-testid="home-weekly-progress"
                    >
                      {(weeklyRow.countedMatches ?? 0)} / {weeklyMe.data?.maxCountedMatches ?? 15} matches
                    </div>
                  </>
                ) : (
                  <div className="kk-type-ui text-white/60" style={{ fontSize: 11, marginTop: 3 }} data-testid="home-weekly-empty">
                    Play Ranked to enter
                  </div>
                )}
              </div>
            </div>
            <hr className="kk-divider" style={{ margin: '8px 0' }} />
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1.5 min-w-0">
                <Clock size={14} className="text-[hsl(var(--kk-gold-bright))] flex-shrink-0" />
                <div className="min-w-0">
                  <div
                    className="kk-type-display text-white leading-none truncate"
                    style={{ fontSize: 13 }}
                    data-testid="home-weekly-countdown"
                  >
                    {countdown}
                  </div>
                  <div
                    className="kk-type-ui uppercase truncate"
                    style={{ fontSize: 9, letterSpacing: '0.20em', color: 'rgba(255,255,255,0.68)', marginTop: 2 }}
                  >
                    Resets Mon 00:00
                  </div>
                </div>
              </div>
              <ChevronRight size={14} className="text-white/40 flex-shrink-0" />
            </div>
          </Panel>
        </div>

        {/* ============ UTILITY: PROFILE / HOW TO PLAY / SETTINGS ============ */}
        <div className="grid grid-cols-3 gap-2" style={{ marginTop: 6 }}>
          <UtilityTile
            icon={<User size={18} />}
            label="Profile"
            testId="home-profile-btn"
            onClick={() => { track(EVT.HOME_CTA_CLICKED, { metadata: { cta: 'profile' } }); onProfile?.(); }}
          />
          <UtilityTile
            icon={<BookOpen size={18} />}
            label="How to Play"
            testId="how-to-play-button"
            onClick={() => { track(EVT.HOME_CTA_CLICKED, { metadata: { cta: 'how_to_play' } }); onTutorial?.(); }}
          />
          <UtilityTile
            icon={<Settings size={18} />}
            label="Settings"
            testId={TID.settingsBtn}
            onClick={() => { track(EVT.HOME_CTA_CLICKED, { metadata: { cta: 'settings' } }); onSettings?.(); }}
          />
        </div>

        {/* ============ FOOTER MOTTO ============ */}
        <div className="text-center" style={{ marginTop: 18 }}>
          <hr
            className="mx-auto"
            style={{
              width: 100, border: 0, height: 1, marginBottom: 6,
              background: 'linear-gradient(90deg, transparent, hsl(var(--kk-gold-deep)) 50%, transparent)',
            }}
          />
          <div
            className="kk-type-ui uppercase italic"
            style={{
              color: 'rgba(255,255,255,0.75)',
              fontSize: 10, letterSpacing: '0.14em', lineHeight: 1.35,
            }}
          >
            &ldquo;Good players play cards.<br />Great players Kickard.&rdquo;
          </div>
        </div>

        <InstallPrompt />
      </div>
    </div>
  );
}

// ------------------------------------------------------------------
// UtilityTile — dark broadcast tile for the utility row.
// ------------------------------------------------------------------
function UtilityTile({ icon, label, testId, onClick }) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      className="kk-panel kk-panel--inset flex flex-col items-center justify-center gap-1 text-white/85 hover:text-[hsl(var(--kk-gold-bright))] active:bg-white/[0.04] transition-colors"
      style={{ minHeight: 64, padding: '10px 6px' }}
    >
      <span aria-hidden>{icon}</span>
      <span
        className="kk-type-display"
        style={{ fontSize: 11, letterSpacing: '0.14em' }}
      >
        {label}
      </span>
    </button>
  );
}

// ------------------------------------------------------------------
// RankCrest — renders the premium tier crest artwork with a LIVE
// division numeral overlay. `tier` and `division` mirror the existing
// Kickard rank representation (rank.currentTier, rank.currentDivision).
// Divisions map 1→I, 2→II, 3→III (same as CompetitiveSummary /
// CompetitiveResultBlock). Legend/unranked render with no numeral.
// ------------------------------------------------------------------
function RankCrest({ tier, division, testId }) {
  const key = tier && TIER_ART[tier] ? tier : null;
  const src = key ? TIER_ART[key] : TIER_ART.gold;
  const numeral = key && key !== 'legend' && division ? DIVISION_ROMAN[Number(division)] : null;
  const accent = key ? (TIER_ACCENT[key] || '#D4AF37') : 'rgba(255,255,255,0.35)';
  return (
    <div
      data-testid={testId}
      className="relative flex-shrink-0"
      style={{ width: 52, height: 52 }}
    >
      <img
        src={src}
        alt=""
        aria-hidden
        style={{
          width: '100%', height: '100%', objectFit: 'contain',
          filter: `drop-shadow(0 3px 10px rgba(0,0,0,0.55)) drop-shadow(0 0 6px ${accent}55)`,
          ...(key ? null : CREST_PLACEHOLDER_STYLE),
        }}
      />
      {numeral && (
        <span
          aria-hidden
          className="kk-type-display"
          style={{
            ...crestNumeralStyle(key),
            color: '#ffffff',
            fontSize: 15,
            letterSpacing: '0.02em',
            textShadow:
              '0 1px 0 rgba(0,0,0,0.85), 0 0 4px rgba(0,0,0,0.9), 0 0 8px rgba(0,0,0,0.6)',
          }}
          data-testid="home-ranked-division-numeral"
        >
          {numeral}
        </span>
      )}
    </div>
  );
}

// ------------------------------------------------------------------
// buildTierLabel — local fallback that matches the shared Kickard
// representation used in CompetitiveSummary.jsx / CompetitiveResultBlock.jsx.
// Server-provided `rank.currentLabel` is always preferred; this is
// only a fallback when the server did not supply one.
// ------------------------------------------------------------------
function buildTierLabel(tier, division) {
  if (!tier || tier === 'unranked') return 'UNRANKED';
  if (tier === 'legend') return 'LEGEND';
  const name = String(tier).toUpperCase();
  const div = DIVISION_ROMAN[Number(division) || 0];
  return div ? `${name} ${div}` : name;
}
